export const RV_API_PREFIX = '/rv-api';
export const RV_ASSETS_PREFIX = '/rv-assets';

const NO_STORE =
  'no-store, no-cache, must-revalidate, post-check=0, pre-check=0';

export class InvalidAssetPrefixError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidAssetPrefixError';
  }
}

/**
 * Browser path for a hen-site API path. Absolute URLs stay on their current
 * host. A relative `/api` path moves under `/rv-api`.
 */
export function prefixHenSitePath(path: string): string {
  if (path.startsWith(`${RV_API_PREFIX}/`) || path === RV_API_PREFIX) {
    return path;
  }
  if (path === '/api') return RV_API_PREFIX;
  if (path.startsWith('/api/') || path.startsWith('/api?')) {
    return `${RV_API_PREFIX}${path.slice('/api'.length)}`;
  }
  return path;
}

/**
 * Relative hen-site URL. An absolute base is an affiliate embed and keeps
 * today's `/api` path on that origin. An empty base stays on the shopper host.
 */
export function henSiteBrowserUrl(baseUrl: string, path: string): string {
  if (baseUrl) return `${baseUrl}${path}`;
  return prefixHenSitePath(path);
}

/**
 * `assetPrefix` for this build. Unset keeps relative `/_next` URLs.
 * The only accepted value is the shared `/rv-assets` path.
 */
export function resolveAssetPrefix(
  raw: string | null | undefined,
): string | undefined {
  if (raw == null) return undefined;
  const value = raw.trim().replace(/\/$/, '');
  if (value === '') return undefined;
  if (value === RV_ASSETS_PREFIX) return RV_ASSETS_PREFIX;
  throw new InvalidAssetPrefixError(
    'ASSET_PREFIX must be /rv-assets when it is set',
  );
}

/** Public files are not covered by Next's assetPrefix. */
export function publicFilePath(
  path: string,
  assetPrefix: string | undefined,
): string {
  const absolute = path.startsWith('/') ? path : `/${path}`;
  if (!assetPrefix) return absolute;
  if (absolute === assetPrefix || absolute.startsWith(`${assetPrefix}/`)) {
    return absolute;
  }
  return `${assetPrefix}${absolute}`;
}

/**
 * 308 target for a slash-less `/rv-api` path. The destination stays under
 * `/rv-api` so the follow-up is not the shopper host's `/api`.
 */
export function slashlessRvApiRedirectPath(pathname: string): string | null {
  if (!pathname.split('/').includes('rv-api')) return null;
  if (pathname.endsWith('/')) return null;
  return `${pathname}/`;
}

export function rvApiRedirects(): {
  source: string;
  destination: string;
  permanent: true;
}[] {
  return [
    { source: '/rv-api', destination: '/rv-api/', permanent: true },
    {
      source: '/:tenant/rv-api',
      destination: '/:tenant/rv-api/',
      permanent: true,
    },
  ];
}

export function rvApiRewrites(): { source: string; destination: string }[] {
  return [
    {
      source: '/:tenant/rv-api/decisions/post-load/',
      destination: '/:tenant/api/decisions/post-load/',
    },
    {
      source: '/:tenant/rv-api/:path*',
      destination: '/api/:path*',
    },
    {
      source: '/rv-api/:path*',
      destination: '/api/:path*',
    },
  ];
}

export function rvApiCacheHeaders(): {
  source: string;
  headers: { key: string; value: string }[];
}[] {
  const rule: {
    headers: { key: string; value: string }[];
  } = {
    headers: [{ key: 'CDN-Cache-Control', value: NO_STORE }],
  };
  return [
    { source: '/rv-api/:path*', ...rule },
    { source: '/:tenant/rv-api/:path*', ...rule },
  ];
}
